import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { cn } from '@/lib/utils';

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  maxDate?: Date;
  minDate?: Date;
  selected?: Date;
};

const defaultClassNames: Record<string, string> = {
  root: 'relative',
  months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
  month: 'space-y-4 relative',
  month_caption: 'flex justify-center pt-1 items-center h-9 mb-2',
  caption_label: 'text-sm font-medium hidden',
  dropdowns: 'flex gap-2 items-center',
  dropdown: 'appearance-none bg-background border border-input rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer',
  dropdown_root: 'relative',
  months_dropdown: 'appearance-none bg-background border border-input rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer',
  years_dropdown: 'appearance-none bg-background border border-input rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer',
  nav: 'absolute top-1 left-0 right-0 flex items-center justify-between px-0 z-10',
  button_previous: 'h-7 w-7 bg-background p-0 opacity-70 hover:opacity-100 inline-flex items-center justify-center rounded-md border border-input cursor-pointer z-20',
  button_next: 'h-7 w-7 bg-background p-0 opacity-70 hover:opacity-100 inline-flex items-center justify-center rounded-md border border-input cursor-pointer z-20',
  month_grid: 'w-full border-collapse space-y-1',
  weekdays: 'flex',
  weekday: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
  week: 'flex w-full mt-2',
  day: 'h-9 w-9 text-center text-sm p-0 relative',
  day_button: 'h-9 w-9 p-0 font-normal rounded-md hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground inline-flex items-center justify-center',
  selected: 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md',
  today: 'bg-accent text-accent-foreground rounded-md',
  outside: 'text-muted-foreground opacity-50',
  disabled: 'text-muted-foreground opacity-50',
  hidden: 'invisible',
};

function Calendar({
  className,
  classNames: classNameOverrides,
  showOutsideDays = true,
  maxDate,
  minDate,
  disabled: disabledFromProps,
  ...props
}: CalendarProps) {
  const disabledDates: { before?: Date; after?: Date } = {};
  if (minDate) {
    disabledDates.before = minDate;
  }
  if (maxDate) {
    disabledDates.after = maxDate;
  }

  // Merge min/max range with any function- or matcher-based disabled passed
  // by the caller. react-day-picker accepts an array of matchers, so we
  // combine the range with the caller's disabled rules.
  const disabledMatchers: NonNullable<React.ComponentProps<typeof DayPicker>['disabled']>[] = [];
  if (Object.keys(disabledDates).length > 0) {
    disabledMatchers.push(disabledDates);
  }
  if (disabledFromProps) {
    if (Array.isArray(disabledFromProps)) {
      disabledMatchers.push(...disabledFromProps);
    } else {
      disabledMatchers.push(disabledFromProps);
    }
  }
  const mergedDisabled = disabledMatchers.length > 0 ? disabledMatchers : undefined;

  const today = new Date();
  const startMonth = minDate || new Date(today.getFullYear() - 100, 0, 1);
  const endMonth = maxDate || new Date(today.getFullYear() + 10, 11, 31);

  const mergedClassNames = { ...defaultClassNames };
  if (classNameOverrides) {
    for (const key of Object.keys(classNameOverrides)) {
      const k = key as keyof typeof classNameOverrides;
      if (classNameOverrides[k]) {
        mergedClassNames[key] = classNameOverrides[k] as string;
      }
    }
  }

  return (
    <DayPicker
      {...props}
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      captionLayout="dropdown"
      startMonth={startMonth}
      endMonth={endMonth}
      disabled={mergedDisabled}
      components={{
        Chevron: ({ orientation }) => {
          if (orientation === 'left') {
            return <ChevronLeft className="h-4 w-4" />;
          }
          return <ChevronRight className="h-4 w-4" />;
        },
      }}
      classNames={mergedClassNames}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
